import { Injectable, inject } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { UserApiService } from '~shared/services/api/general/user.api-service';
import { UserUtils } from '~shared/utils/user.utils';

/**
 * Lit/écrit les préférences utilisateur "à plat" et les persiste côté API.
 *
 * Le tableau `preference` de l'utilisateur mélange deux encodages historiques
 * qui vivent sous la même clé localStorage `user` :
 *   - `preference[0]` est réservé à `{ views: [...] }` (favoris, cf. UserUtils) ;
 *   - les entrées suivantes sont des objets mono-clé (`{ dashboardId: 12 }`,
 *     `{ editMode: true }`, ...) lus par clé.
 *
 * Ce service ne manipule QUE le second encodage : il cherche/ajoute une entrée
 * mono-clé sans jamais réordonner le tableau ni toucher à l'entrée `views`.
 * Casser cet invariant ferait disparaître les vues favorites.
 */
@Injectable({ providedIn: 'root' })
export class UserPreferencesService {
  private readonly userApiService = inject(UserApiService);
  private readonly USER_STORAGE_KEY = 'user';

  /** Lit la valeur d'une préférence mono-clé, ou undefined si absente. */
  read<T = unknown>(key: string): T | undefined {
    const preferences = this.readAll();
    const entry = preferences?.find(
      (item) => item && typeof item === 'object' && key in item
    );
    return entry ? (entry[key] as T) : undefined;
  }

  /**
   * Écrit une préférence mono-clé.
   *
   * localStorage est écrit avant la réponse de l'API (l'app lit la préférence
   * de là, de façon synchrone), mais restauré si le PATCH échoue : « pas
   * enregistré » doit vouloir dire « pas appliqué ». Sans ce rollback, un
   * serveur qui refuse laisserait le réglage actif jusqu'au prochain login,
   * et l'écran de réglages afficherait une erreur tout en ayant changé le
   * comportement.
   *
   * @returns un Observable qui complète après le PATCH. Les erreurs sont
   *   propagées : un écran de réglages doit pouvoir dire à l'utilisateur que
   *   rien n'a été enregistré. Les appelants « fire and forget » les ignorent
   *   explicitement de leur côté.
   */
  write(key: string, value: unknown): Observable<unknown> {
    const raw = localStorage.getItem(this.USER_STORAGE_KEY);
    if (!raw) {
      return of(null);
    }
    const decodedUser = UserUtils.initDecodedUser(raw);
    if (!decodedUser) {
      return of(null);
    }
    if (!Array.isArray(decodedUser.preference)) {
      decodedUser.preference = [{ views: [] }];
    }

    const preferences = decodedUser.preference as Record<string, unknown>[];
    const index = preferences.findIndex(
      (item) => item && typeof item === 'object' && key in item
    );
    if (index >= 0) {
      preferences[index] = { ...preferences[index], [key]: value };
    } else {
      preferences.push({ [key]: value });
    }

    localStorage.setItem(this.USER_STORAGE_KEY, JSON.stringify(decodedUser));

    const uid = decodedUser.id;
    if (typeof uid !== 'number') {
      return of(null);
    }
    const params = new URLSearchParams();
    params.append('preference', JSON.stringify(decodedUser.preference));
    return this.userApiService.update(uid, params).pipe(
      catchError((error) => {
        localStorage.setItem(this.USER_STORAGE_KEY, raw);
        return throwError(() => error);
      })
    );
  }

  private readAll(): Record<string, unknown>[] | undefined {
    const raw = localStorage.getItem(this.USER_STORAGE_KEY);
    if (!raw) {
      return undefined;
    }
    try {
      const preference = UserUtils.initDecodedUser(raw)?.preference;
      return Array.isArray(preference) ? preference : undefined;
    } catch {
      return undefined;
    }
  }
}
